import { useCallback, useRef } from 'react';
import { Platform } from 'react-native';

import { deleteToken } from '../../helpers/auth';
import { unauthenticatedClient } from './../../api/apollo';
import { User } from './Auth';
import { useLoginMutation } from './__generated__/loginUser.mutation.generated';

export const useLogin = () => {
	const abortControllerRef = useRef<AbortController>();

	const [loginUser] = useLoginMutation({
		client: unauthenticatedClient,
	});

	const handleLogin = useCallback(
		async ({
			email,
			username,
			password,
		}: {
			email?: string;
			username?: string;
			password: string;
		}): Promise<{
			user: User;
			accessToken: string;
			refreshToken: string;
			mediaToken: string;
			callbackUrl?: string;
		}> => {
			abortControllerRef.current = new AbortController();
			await deleteToken();

			const result = await loginUser({
				variables: { email, username, password },
				context: { fetchOptions: { signal: abortControllerRef.current.signal } },
			});

			if (result.errors?.length) {
				throw Error(result.errors[0].message);
			}

			if (!result.data) {
				throw Error('No auth data returned');
			}

			validateLoginAbilityOnPlatform(result.data.login.user.role);

			return result.data.login;
		},
		[loginUser],
	);

	const abortLogin: () => void = useCallback(
		() => abortControllerRef.current && abortControllerRef.current.abort(),
		[],
	);

	return { handleLogin, abortLogin };
};

const validateLoginAbilityOnPlatform = (role: string) => {
	const notAllowedError = Error('Not allowed to login on this platform');
	if (Platform.OS === 'web' && role === 'USER') {
		throw notAllowedError;
	}

	if (Platform.OS !== 'web' && role === 'ORGANISATION_ADMIN') {
		throw notAllowedError;
	}
};
